/**
 * "request N" citations in ask-panel replies → in-page links (TRA-1080).
 *
 * The review chat's system prompt makes the model cite evidence as
 * `request N`, where N is the 1-based position of the gateway request in
 * the trajectory — the same position the transcript's request dividers
 * carry (both sides read the server's one sorted trajectory, verbatim).
 * The reply is plain markdown, so the link is added here, client-side,
 * before rendering: no prompt change, and threads restored from
 * sessionStorage get links too.
 *
 * Numbers outside 1..requestCount stay plain text — a link to a request
 * that is not on the page would scroll nowhere.
 */

/** The `href` a linked citation carries; the transcript renders the same id. */
export const REQUEST_HREF_PREFIX = '#request-'

/** DOM id of request N's divider on the review page. */
export function requestAnchorId(n: number): string {
  return `request-${n.toString()}`
}

/** N from a citation href, or null for any other link. */
export function requestFromHref(href: string | undefined): number | null {
  if (href === undefined || !href.startsWith(REQUEST_HREF_PREFIX)) return null
  const n = Number(href.slice(REQUEST_HREF_PREFIX.length))
  return Number.isInteger(n) && n > 0 ? n : null
}

// One number, or a list: "11/12", "3 and 4", "3-5", "3, 4, and 5",
// "2 to 6". Ranges link their endpoints only.
const SEPARATOR = String.raw`(?:\s*,\s*(?:and\s+|or\s+)?|\s*[/&\-–]\s*|\s+(?:and|or|to|through)\s+)`
const CITATION = new RegExp(
  // A citation the prompt wraps in backticks (`request 3`) must lose them,
  // since markdown links do not render inside code spans. The backticks
  // are captured so a citation inside a LONGER code span (one backtick on
  // this side only) is left alone rather than having its span broken.
  String.raw`(\x60?)\b(requests?)(\s+)(\d+(?:${SEPARATOR}\d+)*)\b(\x60?)`,
  'gi',
)

function link(label: string, n: number): string {
  return `[${label}](${REQUEST_HREF_PREFIX}${n.toString()})`
}

// A fence line: up to three spaces of indent, then three or more backticks
// or tildes, then the info string. A backtick fence may not carry a
// backtick in its info string (CommonMark), which keeps a one-line
// ```request 3``` code span from opening a fence that swallows the rest
// of the reply.
const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})(.*)$/
const FENCE_CLOSE = /^ {0,3}(`{3,}|~{3,})\s*$/

/** The fence marker a line opens, or null when it is not an opening fence. */
function fenceMarker(line: string): string | null {
  const match = FENCE_OPEN.exec(line)
  if (match === null) return null
  const marker = match[1] ?? ''
  const info = match[2] ?? ''
  return marker.startsWith('`') && info.includes('`') ? null : marker
}

/** Whether `line` closes a fence opened with `open`: same char, at least as long. */
function closesFence(line: string, open: string): boolean {
  const marker = FENCE_CLOSE.exec(line)?.[1]
  return marker !== undefined && marker[0] === open[0] && marker.length >= open.length
}

interface TextChunk {
  /** Inside a fenced code block, fence lines included. */
  fenced: boolean
  text: string
}

/**
 * The text as alternating prose and fenced-code chunks. Joining them with
 * newlines gives the input back byte for byte. An unclosed fence runs to
 * the end, as it does in markdown.
 */
function splitFencedBlocks(text: string): TextChunk[] {
  const chunks: { fenced: boolean; lines: string[] }[] = []
  let open: string | null = null
  for (const line of text.split('\n')) {
    const marker: string | null = open === null ? fenceMarker(line) : null
    const fenced = open !== null || marker !== null
    const last = chunks[chunks.length - 1]
    if (last !== undefined && last.fenced === fenced) last.lines.push(line)
    else chunks.push({ fenced, lines: [line] })
    if (marker !== null) open = marker
    else if (open !== null && closesFence(line, open)) open = null
  }
  return chunks.map((chunk) => ({ fenced: chunk.fenced, text: chunk.lines.join('\n') }))
}

/**
 * The reply with every in-range `request N` turned into a markdown link.
 *
 * The first number takes the word with it ("[request 11](#request-11)");
 * later numbers in a list link alone ("… /[12](#request-12)"), so the
 * separators read as written. Fenced code blocks are quoted evidence and
 * stay as written: a link there would render as its own markup.
 */
export function linkRequestCitations(text: string, requestCount: number): string {
  if (requestCount <= 0) return text
  return splitFencedBlocks(text)
    .map((chunk) => (chunk.fenced ? chunk.text : linkProse(chunk.text, requestCount)))
    .join('\n')
}

/** `linkRequestCitations` for one stretch of prose (no fences inside). */
function linkProse(text: string, requestCount: number): string {
  return text.replace(
    CITATION,
    (whole: string, open: string, word: string, gap: string, list: string, close: string) => {
      if (open !== close) return whole
      // The word and its gap are folded into the first number's label, so
      // the rebuilt list IS the whole replacement (backticks dropped).
      let first = true
      return list.replace(/\d+/g, (digits) => {
        const n = Number(digits)
        const label = first ? `${word}${gap}${digits}` : digits
        first = false
        return n >= 1 && n <= requestCount ? link(label, n) : label
      })
    },
  )
}
